import { getApiService } from "./api";

import { type User } from "../entities/user/User";
import type { License } from "../entities/user/License";
import { LicenseSchema } from "../entities/user/schema/License.schema";
import { UserSchema } from "../entities/user/schema/User.schema";
import z from "zod";

export interface LoginCredentials {
  email?: string;
  username?: string;
  password: string;
}

export interface LoginResponse {
  status: "success";
  user: User
}

export interface ErrorResponse {
  status: "error";
  message: string;
}

export interface SelfSignUpPayload {
  username: string;
  firstName: string;
  lastName: string;
  organisation: string;
  password: string;
}

export interface TokenStatusResponse {
  status: "success" | "error";
  valid: boolean;
  username?: string;
  message?: string;
}

export class AuthService {

  async login(credentials: LoginCredentials): Promise<LoginResponse> {

    const apiService = getApiService();

    apiService.setUseCredentials(true);

    const response = await apiService.post<LoginResponse | ErrorResponse>(
      "authenticate/signinCookie",
      credentials
    );

    if (response?.status === "success") {
      apiService.markSessionEstablished();
    }

    // making sure the user object was returned 
    if (response?.status === "success" && response.user) {
      return response;
    } else {
      throw new Error((response as ErrorResponse)?.message || "Login failed");
    }
  }


  async getMe(): Promise<User> {
    const apiService = getApiService();

    const me = await apiService.get<LoginResponse | ErrorResponse>("authenticate/me");

    if (me?.status === "success" && me?.user) {
      // Successful /me means the cookie is currently valid. Establish the session
      // so the 9-minute refresh cycle runs after page reloads (not just after login).
      apiService.markSessionEstablished();
      return me.user;
    } else {
      throw new Error((me as ErrorResponse)?.message || "Failed to fetch user data");
    }
  }

  async getUser(): Promise<User> {
    const apiService = getApiService();
    const response = await apiService.getParsed(`/user/account`, { user: UserSchema });
    if (response?.status !== "success" || !response.user) {
      throw new Error((response as ErrorResponse)?.message || "Failed to fetch user data");
    }
    return response.user;
  }

  async getLicense(): Promise<License> {
    const apiService = getApiService();

    const response = await apiService.getParsed(
      "user/license/overview",
      { license: LicenseSchema }
    );

    if (response?.status !== "success" || !response.license) {
      throw new Error((response as ErrorResponse)?.message || "Failed to fetch license data");
    }

    return response.license;
  }


  async logout(): Promise<void> {
    const apiService = getApiService();
    await apiService.post("authenticate/signout");
    apiService.clearSession();
  }

  setHydrating(hydrating: boolean): void {
    getApiService().setHydrating(hydrating);
  }

  async updateDetails(data: {
    firstName: string;
    lastName: string;
    organisation: string;
    emails: string[];
  }): Promise<User> {
    const apiService = getApiService();
    const response = await apiService.patch<LoginResponse | ErrorResponse>(
      "user/account",
      data
    );
    if (response?.status !== "success" || !(response as LoginResponse).user) {
      throw new Error((response as ErrorResponse)?.message || "Failed to update user details");
    }
    return (response as LoginResponse).user;
  }

  async updatePassword(newPassword: string, username: string): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "/user/account/update-password",
      { newPassword, username }
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to change password");
    }
  }


  async selfSignUp(payload: SelfSignUpPayload): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "user/account/self-sign-up",
      payload
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to create account");
    }
  }

  async checkVerifyEmailToken(token: string): Promise<boolean> {
    const apiService = getApiService();
    const response = await apiService.get<TokenStatusResponse>(
      "user/account/verify-email",
      { params: { token } }
    );
    if (response?.status === "error") {
      throw new Error(response.message || "Failed to verify token");
    }
    return Boolean(response?.valid);
  }

  async verifyEmail(token: string, password: string): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "user/account/verify-email",
      { password },
      { params: { token } }
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to verify email");
    }
  }

  async requestPasswordReset(username: string): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "user/account/request-password-reset",
      { username }
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to request password reset");
    }
  }

  async checkResetPasswordToken(token: string): Promise<{ valid: boolean; username?: string }> {
    const apiService = getApiService();
    const response = await apiService.get<TokenStatusResponse>(
      "user/account/reset-password",
      { params: { token } }
    );
    if (response?.status === "error") {
      throw new Error(response.message || "Failed to verify token");
    }
    return { valid: Boolean(response?.valid), username: response?.username };
  }

  async resetPassword(token: string, newPassword: string, username: string): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "user/account/reset-password",
      { newPassword, username },
      { params: { token } }
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to reset password");
    }
  }

  async closeAccount(reason: string): Promise<void> {
    const apiService = getApiService();
    const response = await apiService.post<{ status: string; message?: string } | ErrorResponse>(
      "user/account/close",
      { reason }
    );
    if ((response as ErrorResponse)?.status === "error") {
      throw new Error((response as ErrorResponse).message || "Failed to close account");
    }
  }

  async getCreditCount(): Promise<number> {
    const apiService = getApiService();

    try {
      const response = await apiService.getParsed(
        `/user/license/risk-plus-credits`,
        { license: z.object({ riskPlusReportCredits: z.number().optional().default(0) }) }
      );
      // Extract riskPlusReportCredits from the nested license object
      const creditCount = response?.license?.riskPlusReportCredits ?? 0;
      return creditCount

    } catch (error) {
      console.error(`Error fetching credit count:`, error);
      throw error;
    }
  }
}

let _authService: AuthService | null = null;

export function getAuthService(): AuthService {

  if (!_authService) {
    _authService = new AuthService();
  }
  return _authService;
}
